/**
 * Den ene besked, statuszonen viser. `none` betyder ingen besked.
 *
 * Der er ingen kvitterings-besked: kvitteringen er en pop-up (ADV-233). Zonen viser derfor det
 * blivende — at anmodningen er under behandling — også lige efter en bestilling.
 */
export type IdKortStatusBanner = "none" | "error" | "inProgress";

export interface IdKortStatusInput {
  /**
   * Om medlemmet har en anmodning under behandling.
   *
   * Skal komme fra den friske stamdata-forespørgsel — aldrig fra rodrutens
   * loader-data. Loaderen ligger på portalens ydre layout-rute, og React Router
   * genkører den ikke ved klientnavigation hertil, kun ved en egentlig
   * sideindlæsning. Netop den forældede kilde var grunden til, at den gule
   * besked aldrig kom frem ved demoen.
   */
  harAktivIdKortAnmodning: boolean;
  /**
   * Om bestillingen blev gennemført i denne session.
   *
   * Sidestilles med en aktiv anmodning, og det er med vilje: lige efter en bestilling er dette
   * det eneste, vi VED. Genhentningen af stamdata kan fejle, og så ville beskeden og låsen
   * forsvinde under medlemmet, selvom serveren lige har kvitteret for bestillingen.
   */
  submittedInThisSession: boolean;
  /** Fejlbesked fra sidste indsendelse, ellers null. */
  submitError: string | null;
}

export interface IdKortStatus {
  banner: IdKortStatusBanner;
  formLocked: boolean;
}

/**
 * Afgør hvilken besked statuszonen viser, og om formularen er låst.
 *
 * Prioriteringen er beslutningen: en fejlet indsendelse skal ses, ellers går
 * medlemmet fra siden i tro om, at kortet er bestilt. Ellers vises den gule
 * besked, så snart der ligger en anmodning — uanset om den kom i denne session
 * eller lå der i forvejen. De to kilder sidestilles, fordi de betyder det samme
 * for medlemmet, og fordi den ene holder, når den anden svigter.
 *
 * Låsen følger den aktive anmodning og ikke beskeden: en 409 vises som fejl,
 * men formularen skal stadig være låst, fordi serveren netop har fortalt os, at
 * der ligger en anmodning.
 *
 * Returnerer tilstand, ikke tekst. Teksterne hører i komponenten, så testene
 * handler om logikken og ikke om formuleringer.
 */
export const getIdKortStatus = ({
  harAktivIdKortAnmodning,
  submittedInThisSession,
  submitError,
}: IdKortStatusInput): IdKortStatus => {
  if (submitError) {
    return { banner: "error", formLocked: harAktivIdKortAnmodning };
  }

  if (submittedInThisSession || harAktivIdKortAnmodning) {
    return { banner: "inProgress", formLocked: true };
  }

  return { banner: "none", formLocked: false };
};
